// Enum values shared by the database (pgEnum), the API (zod) and the web app.
// Order matters where noted: it is used for ranking or for status progressions.

export const ACCOUNT_TYPES = ['resident', 'staff'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export const USER_STATUSES = ['invited', 'active', 'deactivated'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const OFFICE_TYPES = ['municipal', 'barangay'] as const;
export type OfficeType = (typeof OFFICE_TYPES)[number];

/** Ordered from least to most access. */
export const OFFICE_ROLES = ['viewer', 'contributor', 'editor', 'office_head'] as const;
export type OfficeRole = (typeof OFFICE_ROLES)[number];
export function roleAtLeast(role: OfficeRole, min: OfficeRole): boolean {
  return OFFICE_ROLES.indexOf(role) >= OFFICE_ROLES.indexOf(min);
}

export const CAPABILITIES = [
  'dispatch',
  'crop_damage',
  'evacuation',
  'tax',
  'civil_registry',
  'permits',
  'queue',
  'facilities',
  'waste',
  'tourism',
  'market_prices',
  'emergency_mode',
  'town_wide_urgent_alerts',
  // Phase 3
  'transparency',
  'ordinances',
  'community_moderation',
] as const;
export type Capability = (typeof CAPABILITIES)[number];

export const POST_TYPES = ['announcement', 'schedule', 'program'] as const;
export type PostType = (typeof POST_TYPES)[number];

export const POST_STATUSES = ['draft', 'pending_approval', 'published', 'archived'] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

export const AUDIENCES = ['town', 'barangays'] as const;
export type Audience = (typeof AUDIENCES)[number];

export const REGISTRATION_STATUSES = ['pending', 'confirmed', 'waitlisted', 'declined', 'cancelled'] as const;
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number];
/** Statuses that hold (or wait for) a seat. */
export const ACTIVE_REGISTRATION_STATUSES = ['pending', 'confirmed', 'waitlisted'] as const;

export const REGISTRATION_FIELD_KINDS = [
  'name',
  'mobile',
  'email',
  'barangay',
  'address',
  'birthdate',
  'age',
  'sex',
  'rsbsa',
  'senior_pwd_id',
  'school',
  'companions',
  'text',
  'dropdown',
  'checkbox',
  'file',
] as const;
export type RegistrationFieldKind = (typeof REGISTRATION_FIELD_KINDS)[number];

export const SEXES = ['female', 'male', 'prefer_not_to_say'] as const;
export type Sex = (typeof SEXES)[number];

export const FOLLOW_TARGETS = ['office', 'barangay', 'category'] as const;
export type FollowTarget = (typeof FOLLOW_TARGETS)[number];

export const PUSH_PROVIDERS = ['webpush', 'fcm', 'apns'] as const;
export type PushProviderName = (typeof PUSH_PROVIDERS)[number];

export const SITE_STATUSES = ['open', 'full', 'closed'] as const;
export type SiteStatus = (typeof SITE_STATUSES)[number];

export const LAYER_KINDS = ['hazard', 'road_closure'] as const;
export type LayerKind = (typeof LAYER_KINDS)[number];

export const HAZARD_TYPES = ['landslide', 'flood', 'other'] as const;
export type HazardType = (typeof HAZARD_TYPES)[number];

export const EMERGENCY_TYPES = [
  'fire',
  'flood',
  'landslide',
  'medical',
  'road_accident',
  'trapped_persons',
  'crime',
  'other',
] as const;
export type EmergencyType = (typeof EMERGENCY_TYPES)[number];

/** Ordered progression shown on the reporter's tracking screen. */
export const EMERGENCY_STATUSES = ['received', 'acknowledged', 'dispatched', 'on_scene', 'resolved', 'closed'] as const;
export type EmergencyStatus = (typeof EMERGENCY_STATUSES)[number];

export const PRIORITIES = ['low', 'normal', 'high', 'critical'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const REPORT_FLAGS = ['prank', 'spam'] as const;
export type ReportFlag = (typeof REPORT_FLAGS)[number];

export const YES_NO_UNKNOWN = ['yes', 'no', 'unknown'] as const;
export type YesNoUnknown = (typeof YES_NO_UNKNOWN)[number];

export const UPDATE_AUTHORS = ['reporter', 'staff', 'system'] as const;
export type UpdateAuthor = (typeof UPDATE_AUTHORS)[number];

export const DISASTER_TYPES = ['typhoon', 'flood', 'landslide', 'frost', 'drought', 'earthquake', 'other'] as const;
export type DisasterType = (typeof DISASTER_TYPES)[number];

export const DISASTER_EVENT_STATUSES = ['active', 'closed'] as const;
export type DisasterEventStatus = (typeof DISASTER_EVENT_STATUSES)[number];

export const CROP_REPORT_STATUSES = ['submitted', 'for_validation', 'validated', 'included', 'not_eligible'] as const;
export type CropReportStatus = (typeof CROP_REPORT_STATUSES)[number];

export const GROWTH_STAGES = ['seedling', 'vegetative', 'flowering', 'ready_for_harvest'] as const;
export type GrowthStage = (typeof GROWTH_STAGES)[number];

export const DAMAGE_EXTENTS = ['partial', 'total'] as const;
export type DamageExtent = (typeof DAMAGE_EXTENTS)[number];

export const DAMAGE_CAUSES = ['typhoon', 'flood', 'landslide', 'frost', 'hail', 'drought', 'pests_disease', 'other'] as const;
export type DamageCause = (typeof DAMAGE_CAUSES)[number];

export const AREA_UNITS = ['sqm', 'ha'] as const;
export type AreaUnit = (typeof AREA_UNITS)[number];

export const SMS_STATUSES = ['sent', 'failed', 'not_configured'] as const;
export type SmsStatus = (typeof SMS_STATUSES)[number];

export const MEDIA_VISIBILITY = ['public', 'restricted'] as const;
export type MediaVisibility = (typeof MEDIA_VISIBILITY)[number];

/** What a restricted media file belongs to; used for access checks. */
export const MEDIA_SCOPES = ['post', 'office', 'lgu', 'registration', 'emergency_report', 'crop_report', 'service', 'request', 'facility', 'tourism', 'transparency', 'community', 'pickup', 'guide'] as const;
export type MediaScope = (typeof MEDIA_SCOPES)[number];

export const AUTH_TOKEN_PURPOSES = ['verify_email', 'reset_password', 'invite'] as const;
export type AuthTokenPurpose = (typeof AUTH_TOKEN_PURPOSES)[number];

export const SESSION_TRANSPORTS = ['cookie', 'bearer'] as const;
export type SessionTransport = (typeof SESSION_TRANSPORTS)[number];

export const LOCALES = ['en', 'fil'] as const;
export type Locale = (typeof LOCALES)[number];

// ---- Phase 2: services, requests, taxes, feedback ----

/** RA 11032 (Ease of Doing Business Act) classification of a government service. */
export const SERVICE_CLASSIFICATIONS = ['simple', 'complex', 'highly_technical'] as const;
export type ServiceClassification = (typeof SERVICE_CLASSIFICATIONS)[number];
/** Maximum processing time in working days for each classification (RA 11032, Sec. 9). */
export const CLASSIFICATION_DAYS: Record<ServiceClassification, number> = { simple: 3, complex: 7, highly_technical: 20 };

/** One tracked request table: online service applications (incl. civil registry), community concerns, formal complaints. */
export const REQUEST_KINDS = ['service', 'community_report', 'complaint'] as const;
export type RequestKind = (typeof REQUEST_KINDS)[number];

/**
 * submitted → in_review → processing → ready (for release) → completed; or needs_info (returned to the
 * resident, clock stopped), rejected, cancelled (by the resident).
 */
export const REQUEST_STATUSES = ['submitted', 'in_review', 'needs_info', 'processing', 'ready', 'completed', 'rejected', 'cancelled'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];
/** Still being worked on by the office (the RA 11032 clock runs). */
export const CLOCK_RUNNING_STATUSES = ['submitted', 'in_review', 'processing'] as const satisfies readonly RequestStatus[];
/** Not finished from the resident's point of view. */
export const OPEN_REQUEST_STATUSES = ['submitted', 'in_review', 'needs_info', 'processing', 'ready'] as const satisfies readonly RequestStatus[];

export const REQUEST_EVENT_TYPES = ['created', 'status', 'note', 'internal_note', 'resident_reply', 'assigned', 'attachment'] as const;
export type RequestEventType = (typeof REQUEST_EVENT_TYPES)[number];

export const ASSESSMENT_TYPES = ['cedula_individual', 'cedula_juridical', 'rpt'] as const;
export type AssessmentType = (typeof ASSESSMENT_TYPES)[number];
export const ASSESSMENT_STATUSES = ['unpaid', 'paid', 'cancelled'] as const;
export type AssessmentStatus = (typeof ASSESSMENT_STATUSES)[number];
